import axios, { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { getToken, clearAuthStorage } from "./storage";
import { store } from "../redux/store";
import { authActions } from "../redux/slices/auth";
import { appStatusActions } from "../redux/slices/appStatus";
import { ApiResponseStatus } from "../shared/enums/ApiResponseStatus";
import { ApiResponseDto } from "../shared/dtos/common/ApiResponseDto";

// Get IP address from environment variable
const serverIp = process.env.REACT_APP_API_BASE_URL;

if (!serverIp) {
  throw new Error(
    "REACT_APP_API_BASE_URL is not defined in your environment variables",
  );
}

// Create an axios instance
const axiosInstance = axios.create({
  baseURL: `${serverIp}/api/v1`,
  timeout: 30000, // 30 seconds in milliseconds
});

// Interceptor to attach token when required
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    const requiresAuth = config.headers?.requiresAuth !== false;

    if (token && requiresAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Remove the custom property before sending the request
    delete config.headers.requiresAuth;

    return config;
  },
  (error) => Promise.reject(error),
);

// Optional: You can intercept the response to handle the timeout error globally
axiosInstance.interceptors.response.use(
  (response: AxiosResponse<ApiResponseDto<any>>) => {
    const data = response.data;

    if (data && data.status) {
      // 1. Token Expiry & Invalidity
      if (
        data.status === ApiResponseStatus.TOKEN_EXPIRED ||
        data.status === ApiResponseStatus.TOKEN_INVALID
      ) {
        if (!window.location.pathname.includes("/auth")) {
          toast.warn(data.message || "Session expired. Please log in again.");
          store.dispatch(authActions.userLogout());
          clearAuthStorage();
        }
        return Promise.reject(new Error(data.message));
      }

      // 2. Global License Guard Interception
      if (
        data.status === ApiResponseStatus.LICENSE_EXPIRED ||
        data.status === ApiResponseStatus.LICENSE_INVALID ||
        data.status === ApiResponseStatus.LICENSE_FAILED_VALIDATION
      ) {
        store.dispatch(
          appStatusActions.showLicenseModal({
            status: data.status,
            message: data.message || "License validation failed.",
          }),
        );
        return Promise.reject(new Error(data.message));
      }
    }

    return response;
  },
  (error) => {
    const responseData = error.response?.data as
      ApiResponseDto<any> | undefined;

    // If the request took longer than 30 seconds
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      console.error("Request timed out after 30 seconds");
      toast.error("Request timed out after 30 seconds");
    }

    if (responseData?.status) {
      if (
        responseData.status === ApiResponseStatus.TOKEN_EXPIRED ||
        responseData.status === ApiResponseStatus.TOKEN_INVALID
      ) {
        if (!window.location.pathname.includes("/auth")) {
          toast.warn(responseData.message || "Session expired.");
          store.dispatch(authActions.userLogout());
          clearAuthStorage();
        }
      } else if (
        responseData.status === ApiResponseStatus.LICENSE_EXPIRED ||
        responseData.status === ApiResponseStatus.LICENSE_INVALID ||
        responseData.status === ApiResponseStatus.LICENSE_FAILED_VALIDATION
      ) {
        store.dispatch(
          appStatusActions.showLicenseModal({
            status: responseData.status,
            message: responseData.message || "License error occurred.",
          }),
        );
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
